import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { HOME_ROUTE } from '../config/routes'

const pageStyles: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#f8fafc',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
}

const containerStyles: React.CSSProperties = {
  width: '100%',
  maxWidth: '400px',
}

const cardStyles: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '0.75rem',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  padding: '2rem',
}

const headerStyles: React.CSSProperties = {
  textAlign: 'center',
  marginBottom: '2rem',
}

const titleStyles: React.CSSProperties = {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '#111827',
  marginBottom: '0.5rem',
}

const subtitleStyles: React.CSSProperties = {
  fontSize: '1rem',
  color: '#6b7280',
}

const formStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
}

const inputGroupStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
}

const labelStyles: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#374151',
}

const inputStyles: React.CSSProperties = {
  padding: '0.75rem',
  borderRadius: '0.5rem',
  border: '1px solid #d1d5db',
  fontSize: '1rem',
  transition: 'border-color 0.2s',
}

const buttonStyles: React.CSSProperties = {
  backgroundColor: '#2563eb',
  color: 'white',
  padding: '0.75rem 1rem',
  borderRadius: '0.5rem',
  border: 'none',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
}

const errorStyles: React.CSSProperties = {
  color: '#dc2626',
  fontSize: '0.875rem',
  marginTop: '0.5rem',
}

const linkStyles: React.CSSProperties = {
  textAlign: 'center',
  marginTop: '1rem',
  fontSize: '0.875rem',
  color: '#6b7280',
}

const linkAnchorStyles: React.CSSProperties = {
  color: '#2563eb',
  textDecoration: 'none',
  fontWeight: '500',
}

interface LoginForm {
  email: string
  password: string
}

const LoginPage: React.FC = () => {
  const [form, setForm] = useState<LoginForm>({ email: '', password: '' })
  const [error, setError] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isAuthenticated, isLoading: authLoading } = useAuth()

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(HOME_ROUTE, { replace: true })
    }
  }, [isAuthenticated, authLoading, navigate])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      await login(form.email, form.password)
      
      // Back to whatever they were trying to reach, or the default home.
      const from = (location.state as any)?.from?.pathname || HOME_ROUTE
      navigate(from, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div style={pageStyles}>
      <div style={containerStyles}>
        <div style={cardStyles}>
          <div style={headerStyles}>
            <h1 style={titleStyles}>Welcome to Spread!</h1>
            <p style={subtitleStyles}>Sign in to your account</p>
          </div>
          
          <form style={formStyles} onSubmit={handleSubmit}>
            <div style={inputGroupStyles}>
              <label htmlFor="email" style={labelStyles}>
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={form.email}
                onChange={handleInputChange}
                style={inputStyles}
                placeholder="Enter your email"
                autoComplete="username"
                inputMode="email"
                required
              />
            </div>

            <div style={inputGroupStyles}>
              <label htmlFor="password" style={labelStyles}>
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={form.password}
                onChange={handleInputChange}
                style={inputStyles}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
            </div>

            {error && (
              <div role="alert" aria-live="assertive" style={errorStyles}>
                {error}
              </div>
            )}

            <div style={{ textAlign: 'right' }}>
              <Link to="/forgot-password" style={{ ...linkAnchorStyles, fontSize: '0.875rem' }}>
                パスワードをお忘れですか？
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                ...buttonStyles,
                ...(isLoading ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
              }}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div style={linkStyles}>
            Don't have an account?{' '}
            <Link to="/signup" style={linkAnchorStyles}>
              Sign up
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage